import {
    TeamOutlined,
    CloseOutlined,
    EditOutlined,
    CheckOutlined,
    UserAddOutlined,
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import { Button, Drawer, Form, Input, Popover, Select, Space } from "antd";
import RolesService from "../../../../../services/excel-addin/RolesService";
import Datatable from "../../../../shared/components/Datatable";
import { UpdateRoleRequest } from "../../../../../models/ExcelAddin/UpdateRoleRequest";
import { CreateRoleRequest } from "../../../../../models/ExcelAddin/CreateRoleRequest";
import styles from "./RoleManagement.module.css";
import notificationUtil from "../../../../shared/utils/NotificationsUtil";
import { NOTIFICATION_TYPES } from "../../../../../config/constants";
import { UserRoleFilter } from "../../../../../models/ExcelAddin/UserRoleFilter";

enum RowAction {
    View = 1,
    Edit,
    Create,
}

const RoleManagement = ({ openNewRoleDrawer, setOpenNewRoleDrawer, editAllowed, data, setData, users, fetchData, isTableLoading }) => {

    const [editedRow, setEditedRow] = useState<Role>();

    //drawer
    const [drawerAction, setDrawerAction] = useState<RowAction>();
    const [drawerTitle, setDrawerTitle] = useState<string>("");
    const [openDrawer, setOpenDrawer] = useState(false);
    const placement = "bottom";

    //form
    const [loadingButton, setLoadingButton] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User>();
    const [usersInput, setUsersInput] = useState<User[]>([]);
    const [deletedUserIds, setDeletedUserIds] = useState<number[]>([]);
    const [selectedFilterUserId, setSelectedFilterUserId] = useState<number>();
    const [editedUserRoleFilters, setEditedUserRoleFilters] = useState<UserRoleFilter[]>([]);

    const [form] = Form.useForm();
    const [isValidForm, setIsValidForm] = useState(false);

    interface Role {
        id: number;
        description: string;
        users: User[];
    }

    interface User {
        id: number;
        userName: string;
        filter: string;
    }

    useEffect(() => {
        if (openNewRoleDrawer) {
            showDrawer(RowAction.Create);
        }
    }, [openNewRoleDrawer]);

    const showUsers = (users: User[]) => {
        return (
            <div>
                {users.map(function (d, id) {
                    return <li key={id}>{d.userName}</li>;
                })}
            </div>
        );
    };

    const onCloseDrawer = () => {
        setOpenDrawer(false);
        setOpenNewRoleDrawer(false);
        resetForm();
    };

    const showDrawer = (action: RowAction) => {
        setDrawerAction(action);
        switch (action) {
            case RowAction.View:
                setDrawerTitle("Role details");
                break;
            case RowAction.Create:
                resetForm();
                setDrawerTitle("New role");
                break;
            case RowAction.Edit:
                setDrawerTitle("Edit role");
                break;
            default:
                break;
        }
        setOpenDrawer(true);
    };

    const onSaveRole = () => {
        setLoadingButton(true);

        if (editedRow?.id) {
            const roleToUpdate: UpdateRoleRequest = {
                description: form.getFieldValue("description"),
                userIds: usersInput.map(x => x.id),
                deletedUserIds: deletedUserIds,
                editedUserRoleFilters: editedUserRoleFilters
            }

            RolesService.update(editedRow.id, roleToUpdate)

                .then(res => {
                    fetchData();
                    onCloseDrawer();
                    notificationUtil.openNotificationDialog({ description: "Role edited successfully", type: NOTIFICATION_TYPES.SUCCESS, logError: false });
                })
                .catch(error => notificationUtil.openNotificationDialog({ description: error.response.data, type: NOTIFICATION_TYPES.ERROR, logError: false }))
                .finally(() => {
                    {
                        setLoadingButton(false);
                    }
                });
        } else {
            const newRole: CreateRoleRequest = {
                description: form.getFieldValue("description"),
                userIds: usersInput.map(x => x.id),
            }
            RolesService.create(newRole)
                .then(res => {
                    fetchData();
                    onCloseDrawer();
                    notificationUtil.openNotificationDialog({ description: "Role created successfully", type: NOTIFICATION_TYPES.SUCCESS, logError: false });
                })
                .catch(error => notificationUtil.openNotificationDialog({ description: error.response.data, type: NOTIFICATION_TYPES.ERROR, logError: false }))
                .finally(() => {
                    setLoadingButton(false);
                });
        }
    }

    const onDeleteRole = (id: number) => {
        RolesService.remove(id)
            .then((response) => {
                fetchData();
                notificationUtil.openNotificationDialog({ description: "Role deleted successfully", type: NOTIFICATION_TYPES.SUCCESS, logError: false });
            })
            .catch(error => notificationUtil.openNotificationDialog({ description: error.response.data, type: NOTIFICATION_TYPES.ERROR, logError: false }));
    };


    const onDoubleClickRow = (record: Role) => {
        showDrawer(RowAction.View);
        setForm(record);
        setEditedRow(record);
    };

    const onEditRole = (record: Role) => {
        showDrawer(RowAction.Edit);
        setForm(record);

        setEditedRow(record);
    };

    const setForm = (role: Role) => {
        form.setFieldsValue({
            description: role.description,
        });
        setUsersInput(role.users);
    }


    const resetForm = () => {
        form.resetFields()
        setSelectedUser(undefined);
        setEditedRow(undefined);
        setUsersInput([]);
        setDeletedUserIds([]);
        setSelectedFilterUserId(undefined);
        setEditedUserRoleFilters([]);
        setIsValidForm(false);
    };

    const onAddUserClick = () => {
        if (selectedUser) {
            setUsersInput([...usersInput, selectedUser]);
            setDeletedUserIds(deletedUserIds.filter(id => id !== selectedUser.id));
            setSelectedUser(null);
        }
        onFieldsChange();
    }

    const onDeleteUser = (id: number) => {
        setUsersInput(usersInput.filter(u => u.id !== id));
        setDeletedUserIds([...deletedUserIds, id]);
        onFieldsChange();
    };

    const onEditFilter = (record: User) => {
        setSelectedFilterUserId(record.id);
    }

    const onFilterChange = (value: string) => {
        setUsersInput(usersInput.map(u => {
                if (u.id === selectedFilterUserId) {
                    return {
                        ...u,
                        filter: value
                    }
                }
                return u;
            })
        );

        //if the filter already exists, update it, otherwise add it
        const existingFilter = editedUserRoleFilters.find(f => f.userId === selectedFilterUserId && f.roleId === editedRow.id);
        if (existingFilter) {
            setEditedUserRoleFilters(editedUserRoleFilters.map(f => {
                if (f.userId === selectedFilterUserId && f.roleId === editedRow.id) {
                    return {
                        ...f,
                        filter: value
                    }
                }
                return f;
            }));
        } else {
            setEditedUserRoleFilters([...editedUserRoleFilters, { userId: selectedFilterUserId, roleId: editedRow.id, filter: value }]);
        }

        onFieldsChange();
    }

    const onFieldsChange = () => {
        setIsValidForm(form.getFieldsError().filter(({ errors }) => errors.length).length === 0);
    }

    const scroll = { y: "calc(100vh - 320px)" };

    const columns = [
        {
            title: "Description",
            dataIndex: "description",
            key: "description",
            filters: [],
            filterMode: "tree",
        },
        {
            title: "Users",
            dataIndex: "id",
            key: "id",
            filters: [],
            filterMode: "tree",
            render: (val, rec) => (
                <Popover content={() => showUsers(rec.users)} title="Users">
                    <Button icon={<TeamOutlined />} className={"active-btn"} size="small">
                        Users
                    </Button>
                </Popover>
            ),
        },
    ];

    const editUsersColumns = [
        {
            title: "User Name",
            dataIndex: "userName",
            key: "userName",
            filters: [],
            filterMode: "tree",
            filterSearch: true,
            sorter: (a, b) => {
                return a.userName.localeCompare(b.userName)
            },
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
            filters: [],
            filterMode: "tree",
            filterSearch: true,
            sorter: (a, b) => {
                return a.email?.localeCompare(b.email)
            }
        },
        {
            title: "Filter",
            dataIndex: "filter",
            key: "filter",
            render(value, record, index) {
                if (selectedFilterUserId === record.id) {
                    return <Input value={value} onChange={(e) => onFilterChange(e.target.value)} />
                } else {
                    return value;
                }
            }
        }
    ];

    return (
        <div>
            <Datatable
                scroll={scroll}
                tableColumns={columns}
                data={data}
                setData={setData}
                isTableLoading={isTableLoading}
                editRowEnabled={editAllowed}
                deleteRowEnabled={editAllowed}
                onEditFunc={onEditRole}
                onDeleteFunc={onDeleteRole}
                onDoubleClickRow={onDoubleClickRow}
                showActionsColumn={editAllowed}
                paginationDisabled={true}
            ></Datatable>
            <Drawer
                width={"400px"}
                getContainer={false}
                className={styles.drawer}
                title={drawerTitle}
                placement={placement}
                onClose={onCloseDrawer}
                visible={openDrawer}
                closable={false}
                height={"92%"}
                extra={
                    <Space>
                        {RowAction?.View === drawerAction && (
                            <>
                                <Button
                                    onClick={() => showDrawer(RowAction.Edit)}
                                    icon={<EditOutlined />}
                                    className={"active-btn"}
                                    size="small"
                                    disabled={!editAllowed}
                                    hidden={!editAllowed}
                                >
                                    Edit
                                </Button>
                                <Button
                                    type="ghost"
                                    onClick={onCloseDrawer}
                                    icon={<CloseOutlined />}
                                    className={styles.green_btn}
                                    size="small"
                                />
                            </>
                        )}
                        {RowAction?.Create === drawerAction && (
                            <>
                                <Button
                                    onClick={onSaveRole}
                                    icon={<CheckOutlined />}
                                    size="small"
                                    loading={loadingButton}
                                    disabled={!isValidForm}
                                >
                                    Save
                                </Button>
                                <Button
                                    type="ghost"
                                    onClick={onCloseDrawer}
                                    icon={<CloseOutlined />}
                                    size="small"
                                />
                            </>
                        )}
                        {RowAction?.Edit === drawerAction && (
                            <>
                                <Button
                                    onClick={onSaveRole}
                                    icon={<CheckOutlined />}
                                    size="small"
                                    loading={loadingButton}
                                    disabled={!isValidForm}
                                >
                                    Update
                                </Button>
                                <Button
                                    type="ghost"
                                    onClick={onCloseDrawer}
                                    icon={<CloseOutlined />}
                                    size="small"
                                />
                            </>
                        )}
                    </Space>
                }
            >
                <Form form={form} onFieldsChange={onFieldsChange} disabled={RowAction.View === drawerAction ? true : false} layout="vertical">
                    <div className={styles.columnsWrap}>
                        <div className={styles.halfWidth}>
                            <Form.Item label="Description:" name="description"
                                       rules={[
                                           {
                                               required: true,
                                           },
                                       ]}>
                                <Input
                                    allowClear
                                    placeholder="enter value"
                                ></Input>
                            </Form.Item>
                            <Form.Item label="Add Users:">
                                <div
                                    className={styles.flexRow}
                                >
                                    <Select
                                        allowClear
                                        showSearch
                                        className={styles.fullWidth}
                                        placeholder="Please select"
                                        filterSort={(optionA, optionB) =>
                                            (optionA!.label as unknown as string)
                                                .toLowerCase()
                                                .localeCompare(
                                                    (optionB!.label as unknown as string).toLowerCase()
                                                )
                                        }
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                                        }
                                        options={users
                                            .filter(u => {
                                                return !usersInput.map(u => u.id).includes(u.id)
                                            })
                                            .map(u => {
                                                return {
                                                    label: u.userName,
                                                    value: u.id,
                                                }
                                            })
                                        }
                                        value={selectedUser?.id}
                                        onChange={(userId) => {
                                            var user = users.find(u => u.id === userId);
                                            setSelectedUser(user);
                                        }}
                                    />
                                    <Button
                                        icon={<UserAddOutlined />}
                                        type="primary"
                                        className={styles.green_btn}
                                        onClick={() => onAddUserClick()}
                                    />
                                </div>
                            </Form.Item>
                        </div>
                        <div className={styles.halfWidth}>
                            <Form.Item label="Edit Users:">
                                <Datatable
                                    scroll={scroll}
                                    tableColumns={editUsersColumns}
                                    data={usersInput}
                                    setData={setUsersInput}
                                    isTableLoading={isTableLoading}
                                    editRowEnabled={editAllowed && RowAction?.Edit === drawerAction}
                                    deleteRowEnabled={editAllowed}
                                    onEditFunc={onEditFilter}
                                    onDeleteFunc={onDeleteUser}
                                    showActionsColumn={editAllowed}
                                ></Datatable>
                            </Form.Item>
                        </div>
                    </div>
                </Form>
            </Drawer>
        </div>
    );
};

export { RoleManagement };
